import React, { useState, useRef, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, doc, getDoc } from "firebase/firestore";
import { GoogleGenAI } from "@google/genai";
import { db, auth } from "../firebaseConfig";
import { SupportTicket, SupportMessage, SystemSettings } from '../types';
import { Icons } from '../constants';

interface SupportProps {
  systemSettings: SystemSettings | null;
}

const Support: React.FC<SupportProps> = ({ systemSettings }) => {
  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [input, setInput] = useState('');
  const [isTypingAI, setIsTypingAI] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<any>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTypingAI, activeTicket?.isAdminTyping]);

  // Broadcast user typing status
  useEffect(() => {
    if (!activeTicket || !auth.currentUser) return;
    
    const updateTypingStatus = async (isTyping: boolean) => {
      try {
        await updateDoc(doc(db, 'supportTickets', activeTicket.id), {
          isUserTyping: isTyping
        });
      } catch (err) { /* Silence errors */ }
    };

    if (input.trim().length > 0) {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      updateTypingStatus(true);
      typingTimeoutRef.current = setTimeout(() => {
        updateTypingStatus(false);
      }, 2500);
    } else {
      updateTypingStatus(false);
    }
  }, [input, activeTicket?.id]);

  // Find or create user ticket (Keep history by finding the latest ticket)
  useEffect(() => {
    if (!auth.currentUser) return;
    const q = query(
      collection(db, 'supportTickets'), 
      where('userId', '==', auth.currentUser.uid), 
      orderBy('updatedAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const ticketData = { id: snap.docs[0].id, ...snap.docs[0].data() } as SupportTicket;
        setActiveTicket(ticketData);
        
        // Clear user unread status when they view the ticket
        if (ticketData.hasUnreadUser) {
          updateDoc(doc(db, 'supportTickets', ticketData.id), {
            hasUnreadUser: false
          });
        }
      } else {
        setActiveTicket(null);
      }
    }, () => {}); 
    return () => unsub();
  }, []);

  // Sync messages
  useEffect(() => {
    if (!activeTicket) {
      setMessages([]);
      return;
    }
    const q = query(collection(db, 'supportTickets', activeTicket.id, 'messages'), orderBy('timestamp', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const serverMessages = snap.docs.map(d => ({ id: d.id, ...d.data() } as SupportMessage));
      setMessages(serverMessages);
    }, () => {}); 
    return () => unsub();
  }, [activeTicket?.id]);

  const handleSend = async () => {
    if (!input.trim() || !auth.currentUser) return;
    const text = input;
    setInput('');

    // Optimistic UI: Add user message immediately
    const tempMsgId = 'temp-' + Date.now();
    const tempMsg: SupportMessage = {
      id: tempMsgId,
      senderId: auth.currentUser.uid,
      senderName: auth.currentUser.displayName || 'Trader',
      senderRole: 'user',
      text,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, tempMsg]);

    let ticketId = activeTicket?.id;

    try {
      if (!ticketId) {
        const ticketData = {
          userId: auth.currentUser.uid,
          userEmail: auth.currentUser.email,
          userName: auth.currentUser.displayName || 'Trader',
          subject: text.slice(0, 30) + '...',
          status: 'open',
          lastMessage: text,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          hasUnreadAdmin: true,
          hasUnreadUser: false,
          isAdminTyping: false,
          isUserTyping: false
        };
        const ticketRef = await addDoc(collection(db, 'supportTickets'), ticketData);
        ticketId = ticketRef.id;
        await addDoc(collection(db, 'supportTickets', ticketId, 'messages'), {
          senderId: auth.currentUser.uid,
          senderName: auth.currentUser.displayName || 'Trader',
          senderRole: 'user',
          text,
          timestamp: Date.now()
        });
      } else {
        await updateDoc(doc(db, 'supportTickets', ticketId), {
          isUserTyping: false
        });

        await addDoc(collection(db, 'supportTickets', ticketId, 'messages'), {
          senderId: auth.currentUser.uid,
          senderName: auth.currentUser.displayName || 'Trader',
          senderRole: 'user',
          text,
          timestamp: Date.now()
        });
        await updateDoc(doc(db, 'supportTickets', ticketId), {
          updatedAt: serverTimestamp(),
          lastMessage: text,
          hasUnreadAdmin: true
        });
      }

      // AI Response Logic
      setIsTypingAI(true);
      const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
      const siteName = systemSettings?.siteName || 'BTCC-AI';
      
      const appContext = `
        Site Name: ${siteName}
        Support Email: ${systemSettings?.siteEmail || 'support@neural.net'}
        Office Address: ${systemSettings?.officeAddress || 'Global Remote Hub'}
        Phone: ${systemSettings?.phoneNumber || 'Encrypted Channel'}
        Minimum Deposit: $${systemSettings?.minDeposit || 100}
        Minimum Withdrawal: $${systemSettings?.minWithdrawal || 50}
        Referral Commission: ${systemSettings?.referralPercent || 10}%
        Trading Bot: Autonomous high-frequency trading bot with 4.2ms latency.
        Account Types: Demo (virtual funds) and Live (real capital).
        Security: 2FA (Google Authenticator), KYC verification, ECC-512 encryption.
        Withdrawal Networks: ERC20, TRC20, BEP20.
      `;

      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: text,
        config: {
          systemInstruction: `
            You are the ${siteName} Support Intelligence. 
            Tone: Professional, helpful, slightly cyberpunk/futuristic.
            Context: ${appContext}
            
            Guidelines:
            1. Answer user questions accurately based on the provided context.
            2. If a user asks about their balance or specific trades, explain that you are an AI and they should check their "Overview" or "Ledger" sections, or wait for a human Admin.
            3. For technical issues, give basic troubleshooting (refresh, check connection) and say an Admin is monitoring the terminal.
            4. Always maintain the ${siteName} brand identity.
            5. Keep responses concise and impactful.
          `,
        },
      });
      const aiText = response.text || "Connection glitch. Re-transmitting...";
      await addDoc(collection(db, 'supportTickets', ticketId, 'messages'), {
        senderId: 'ai-engine',
        senderName: 'Neural Engine',
        senderRole: 'ai',
        text: aiText,
        timestamp: Date.now()
      });
      setIsTypingAI(false);
    } catch (err) {
      console.error(err);
      setIsTypingAI(false);
    }
  };

  const renderMessages = () => {
    const rendered: React.ReactNode[] = [];
    let lastDate: string | null = null;

    messages.forEach((msg, idx) => {
      const msgDate = new Date(msg.timestamp).toLocaleDateString();
      if (msgDate !== lastDate) {
        rendered.push(
          <div key={`date-${msg.id}`} className="flex items-center justify-center my-6">
            <div className="h-px bg-white/5 flex-1"></div>
            <span className="mx-4 text-[8px] font-black text-[#444] uppercase tracking-[0.4em]">{msgDate === new Date().toLocaleDateString() ? 'TODAY' : msgDate}</span>
            <div className="h-px bg-white/5 flex-1"></div>
          </div>
        );
        lastDate = msgDate;
      }

      rendered.push(
        <div key={msg.id} className={`flex items-start gap-3 ${msg.senderRole === 'user' ? 'flex-row-reverse' : ''} animate-in slide-in-from-bottom-2 duration-300`}>
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-[8px] shrink-0 border ${
            msg.senderRole === 'ai' ? 'bg-[#AEB877]/10 border-[#AEB877]/30 text-[#AEB877]' : 
            msg.senderRole === 'admin' ? 'bg-white/10 border-white/30 text-white' : 
            'bg-white/5 border-white/10 text-white'
          }`}>
            {msg.senderRole === 'ai' ? 'AI' : msg.senderRole === 'admin' ? 'ADM' : 'YOU'}
          </div>
          <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-[10px] font-medium leading-relaxed border ${
            msg.senderRole === 'user' ? 'bg-white border-white/5 text-black rounded-tr-none' : 
            msg.senderRole === 'admin' ? 'bg-[#AEB877]/5 border-[#AEB877]/20 text-[#AEB877] rounded-tl-none' :
            'bg-white/5 border-white/5 text-white/80 rounded-tl-none backdrop-blur-sm'
          }`}>
            {msg.text}
            <div className={`text-[7px] mt-1 opacity-40 uppercase font-black ${msg.senderRole === 'user' ? 'text-right' : 'text-left'}`}>
              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      );
    });

    return rendered;
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-500 pb-20">
      <div className="glass-card p-4 md:p-6 flex flex-col md:flex-row items-center gap-4 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-48 h-48 bg-[#AEB877]/[0.03] rounded-full blur-[60px] -mr-24 -mt-24 group-hover:bg-[#AEB877]/[0.05] transition-colors"></div>
        
        <div className="relative">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-[24px] bg-gradient-to-br from-white/10 to-white/5 border border-white/10 flex items-center justify-center text-[#AEB877] shadow-2xl relative z-10">
             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          </div>
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#AEB877] rounded-lg flex items-center justify-center text-black shadow-[0_0_15px_rgba(174,184,119,0.3)] z-20 animate-pulse">
             <div className="w-1.5 h-1.5 rounded-full bg-black"></div>
          </div>
        </div>

        <div className="text-center md:text-left relative z-10">
          <h1 className="text-xl md:text-2xl font-black text-white tracking-tighter uppercase">Customer Support</h1>
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-1">
            <p className="text-[8px] font-black text-white/20 uppercase tracking-[0.3em]">AI Support Interface</p>
            <span className="w-1 h-1 bg-white/10 rounded-full"></span>
            <span className="text-[7px] font-black text-[#AEB877] uppercase tracking-[0.2em] bg-[#AEB877]/10 px-2 py-0.5 rounded-md border border-[#AEB877]/20">Online_Sync</span>
          </div>
        </div>
      </div>

      {/* Contact Info Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="glass-card p-4 relative overflow-hidden group shadow-2xl">
          <p className="text-[7px] font-black text-white/20 uppercase tracking-[0.4em] mb-1">Office Address</p>
          <p className="text-[10px] font-black text-white group-hover:text-[#AEB877] transition-colors tracking-tighter">{systemSettings?.officeAddress || 'Global Remote Hub'}</p>
        </div>
        <div className="glass-card p-4 relative overflow-hidden group shadow-2xl">
          <p className="text-[7px] font-black text-white/20 uppercase tracking-[0.4em] mb-1">Email Address</p>
          <p className="text-[10px] font-black text-white group-hover:text-[#AEB877] transition-colors tracking-tighter">{systemSettings?.siteEmail || 'support@neural.net'}</p>
        </div>
        <div className="glass-card p-4 relative overflow-hidden group shadow-2xl">
          <p className="text-[7px] font-black text-white/20 uppercase tracking-[0.4em] mb-1">Phone Line</p>
          <p className="text-[10px] font-black text-white group-hover:text-[#AEB877] transition-colors tracking-tighter">{systemSettings?.phoneNumber || 'Encrypted Channel'}</p>
        </div>
      </div>
      
      <div className="glass-card overflow-hidden shadow-2xl flex flex-col h-[500px] relative border-white/5">
        <div className="p-4 flex items-center justify-between border-b border-white/5 bg-white/[0.02] relative z-10">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 flex items-center justify-center border bg-[#AEB877]/10 border-[#AEB877]/30 text-[#AEB877] rounded-xl">
                <Icons.Support />
             </div>
             <div>
                <h3 className="font-black text-white uppercase tracking-tighter text-lg leading-none">SUPPORT TERMINAL</h3>
                <div className="flex items-center gap-1.5 mt-1">
                   <span className="w-1 h-1 bg-[#AEB877] animate-pulse shadow-[0_0_8px_rgba(174,184,119,0.5)] rounded-full"></span>
                   <span className="text-[8px] font-black text-white/20 uppercase tracking-[0.4em]">AI Support Active</span>
                </div>
             </div>
          </div>
        </div>
        
        <div className="flex-1 p-4 md:p-6 overflow-y-auto space-y-4 no-scrollbar relative z-10">
           {messages.length === 0 && (
             <div className="text-center py-20">
                <p className="text-[10px] font-black text-white/10 uppercase tracking-[0.6em]">No Chat History Found</p>
                <p className="text-[9px] text-white/5 font-black mt-2 uppercase tracking-[0.3em]">Type in your message</p>
             </div>
           )}
           {renderMessages()}
           {activeTicket?.isAdminTyping && (
             <div className="flex items-start gap-4 animate-in fade-in duration-500">
                <div className="w-10 h-10 bg-white/10 border border-white/30 flex items-center justify-center text-white text-[9px] font-black rounded-xl">ADM</div>
                <div className="bg-black/40 border border-[#AEB877]/20 px-4 py-3 rounded-2xl rounded-tl-none text-[9px] font-black uppercase tracking-[0.4em] text-[#AEB877] flex items-center gap-2">
                   <span className="w-1 h-1 bg-[#AEB877] animate-bounce" style={{ animationDelay: '0ms' }}></span>
                   <span className="w-1 h-1 bg-[#AEB877] animate-bounce" style={{ animationDelay: '150ms' }}></span>
                   <span className="w-1 h-1 bg-[#AEB877] animate-bounce" style={{ animationDelay: '300ms' }}></span>
                   Admin_is_typing...
                </div>
             </div>
           )}
           {isTypingAI && (
             <div className="flex items-start gap-4 animate-pulse">
                <div className="w-10 h-10 bg-[#AEB877]/10 flex items-center justify-center text-[#AEB877] text-[9px] font-black rounded-xl">AI</div>
                <div className="bg-black/40 border border-white/5 px-4 py-3 rounded-2xl rounded-tl-none text-[9px] font-black uppercase tracking-[0.4em] text-[#AEB877]">Processing_Neural_Data...</div>
             </div>
           )}
           <div ref={chatEndRef} />
        </div>
        
        <div className="p-6 border-t border-white/5 bg-white/[0.02] relative z-10">
           <div className="relative flex items-center gap-4">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Transmit message to terminal..."
                className="flex-1 bg-black/40 border border-white/10 py-4 px-6 text-white focus:outline-none focus:border-[#AEB877]/50 text-xs font-black outline-none rounded-xl tracking-tight"
              />
              <button 
                onClick={handleSend}
                className="bg-white text-black p-4 hover:bg-[#AEB877] transition-all shadow-2xl rounded-xl active:scale-95"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              </button>
           </div>
        </div>
      </div>
    </div>
  );
};

export default Support;